/**
 * The dashboard's French text (Belgian usage, "vous"), typed against the Dutch
 * source. `\u00a0` is the non-breaking space French puts before ":" and "?".
 * Counts have a `_many` form for a million and up (see `./types`); written in
 * digits it reads as the `_other` form.
 */

import type { nl } from "./nl";
import type { Messages } from "./types";

export const fr: Messages<typeof nl> = {
  common: {
    loading: "Chargement",
    loadingEllipsis: "Chargement…",
    tryAgain: "Réessayer",
    cancel: "Annuler",
    close: "Fermer",
    save: "Enregistrer",
    delete: "Supprimer",
    edit: "Modifier",
    actionsFor: "Actions pour {{name}}",
    on: "Activé",
    off: "Désactivé",
    never: "Jamais",
    pageRange: "{{from, number}}–{{to, number}} sur {{total, number}}",
    resetFilters: "Réinitialiser",
  },
  toast: {
    region: "Notifications",
  },
  language: {
    label: "Langue",
  },
  nav: {
    main: "Menu principal",
    connector: "Connecteur",
    apps: "Apps",
    home: "Accueil",
    analytics: "Statistiques",
    tools: "Outils",
    docs: "Documentation",
    settings: "Paramètres",
    personal: "Personnel",
    organization: "Organisation",
    developer: "Développeurs",
    general: "Général",
    account: "Compte",
    people: "Personnes et équipes",
    roadmap: "Feuille de route",
    activity: "Journal d'activité",
    about: "À propos",
    viewAs: "Voir en tant que",
  },
  shell: {
    skipToContent: "Aller au contenu",
    openMenu: "Ouvrir le menu",
    menu: "Menu",
    menuDescription: "Les pages du tableau de bord, la documentation et votre compte.",
    homeLink: "{{app}}, page d'accueil",
    builtBy: "Réalisé par",
    veltaNewTab: "Velta (s'ouvre dans un nouvel onglet)",
    collapseMenu: "Réduire le menu",
    expandMenu: "Développer le menu",
  },
  environment: {
    readOnly: "Lecture seule",
    title: "Environnement {{name}}, distinct de la production.",
    titleReadOnly:
      "Environnement {{name}}, distinct de la production\u00a0: il lit les données réelles de De Ceuster, mais ne modifie jamais rien dans Odoo ni dans un autre système.",
  },
  accountMenu: {
    fallbackName: "Compte",
    signOut: "Se déconnecter",
    persona: "Persona (local)",
    personaNote: "Uniquement dans un environnement de test local",
  },
  session: {
    reconnecting: "Reconnexion…",
    serverDown: "Le serveur ne répond pas. Cette page reprendra d'elle-même dès son retour.",
  },
  errorPage: {
    title: "Une erreur est survenue",
    body: "Rechargez la page pour réessayer.",
    reload: "Recharger",
  },
  notFound: {
    title: "Page introuvable",
    home: "Retour à l'accueil",
  },
  connector: {
    microsoftDetail: "Connexion avec votre compte Microsoft De Ceuster",
    localLabel: "Test local",
    localDetail: "Sans connexion, uniquement sur cet ordinateur",
    offDetail: "Le connecteur n'est pas encore activé",
    odooOnDetail: "Claude lit l'Odoo de De Ceuster, en lecture seule",
    odooConnectorOffDetail: "Tant que le connecteur est désactivé",
    odooNotConnectedDetail: "Jusqu'à ce qu'Odoo soit connecté",
  },
  usage: {
    lastDays_one: "Aujourd'hui",
    lastDays_many: "{{count}} derniers jours",
    lastDays_other: "{{count}} derniers jours",
    noCalls: "Aucun appel du connecteur au cours des {{count}} derniers jours.",
    calls: "Appels",
    activeUsers: "Utilisateurs actifs",
    errors: "Erreurs",
  },
  chart: {
    successful: "Réussis",
    errors: "Erreurs",
    calls_one: "{{count, number}} appel",
    calls_many: "{{count, number}} appels",
    calls_other: "{{count, number}} appels",
    errorCount_one: "{{count, number}} erreur",
    errorCount_many: "{{count, number}} erreurs",
    errorCount_other: "{{count, number}} erreurs",
    summary: "Appels par jour sur {{days}} jours\u00a0: {{calls}}, {{errors}}.",
    dayDescription: "{{day}}\u00a0: {{calls}}, {{errors}}",
    noCallsDay: "{{day}}\u00a0: aucun appel",
    errorDayDescription: "{{day}}\u00a0: {{errors}} sur {{calls}} ({{share}})",
    measure: {
      label: "Affichage",
      count: "Nombre",
      percent: "Pourcentage",
    },
  },
  table: {
    sortedAscending: "tri croissant",
    sortedDescending: "tri décroissant",
  },
  filters: {
    search: "Rechercher",
    empty: "Aucun résultat.",
    clear: "Effacer",
    toolCount_one: "{{count, number}} outil",
    toolCount_many: "{{count, number}} outils",
    toolCount_other: "{{count, number}} outils",
    personCount_one: "{{count, number}} personne",
    personCount_many: "{{count, number}} personnes",
    personCount_other: "{{count, number}} personnes",
    actionCount_one: "{{count, number}} action",
    actionCount_many: "{{count, number}} actions",
    actionCount_other: "{{count, number}} actions",
  },
  period: {
    label: "Période",
    last: {
      week: "Semaine écoulée",
      month: "Mois écoulé",
      quarter: "Trimestre écoulé",
    },
  },
  timeRange: {
    label: "Plage horaire",
    value: "Plage horaire\u00a0: {{range}}",
    custom: "Personnalisée",
    range: "du {{from}} au {{to}}",
    from: "Du",
    to: "Au",
    startDay: "Jour de début",
    endDay: "Jour de fin",
    startTime: "Heure de début",
    endTime: "Heure de fin",
    chooseDay: "Choisir un jour",
    apply: "Appliquer",
    belgianTime: "En heure belge. La fin inclut toute cette minute.",
    problem: {
      day: "Choisissez un jour pour le début et pour la fin.",
      time: "Tapez une heure comme 08:30, entre 00:00 et 23:59.",
      order: "La fin doit venir après le début.",
      tooLong: "Choisissez une plage d'un an au plus.",
    },
  },
  dayPicker: {
    value: "{{label}}\u00a0: {{day}}",
    previousMonth: "Mois précédent",
    nextMonth: "Mois suivant",
    clear: "Effacer",
  },
  home: {
    morning: "Bonjour",
    afternoon: "Bonjour",
    evening: "Bonsoir",
    status: "État",
    odooTools: "Outils Odoo",
    statusLoadError: "Impossible de charger l'état du connecteur.",
    usageLoadError: "Impossible de charger l'utilisation du connecteur.",
    docsTitle: "Comment utiliser le connecteur dans Claude",
    docsBody:
      "Connectez Claude une seule fois avec votre compte Microsoft De Ceuster, puis posez simplement vos questions sur les clients, les grues, les commandes et les factures. La documentation explique comment faire, ce que vous pouvez demander et ce que le connecteur conserve.",
    docsLink: "Lire la documentation",
    issues: {
      title: "Échecs à analyser",
      failedLastWeek_one: "{{count, number}} échec au cours des 7 derniers jours",
      failedLastWeek_many: "{{count, number}} échecs au cours des 7 derniers jours",
      failedLastWeek_other: "{{count, number}} échecs au cours des 7 derniers jours",
      lastFailed: "dernier échec le {{time}}",
      more_one: "Encore {{count, number}} problème à analyser",
      more_many: "Encore {{count, number}} problèmes à analyser",
      more_other: "Encore {{count, number}} problèmes à analyser",
      acknowledged_one: "{{count, number}} en cours chez Velta",
      acknowledged_many: "{{count, number}} en cours chez Velta",
      acknowledged_other: "{{count, number}} en cours chez Velta",
    },
  },
  tools: {
    retired: {
      label: "Plus disponible",
      hint: "DC Data ne propose plus cet outil. Ses appels antérieurs restent enregistrés et visibles.",
    },
    description: "Les outils que Claude peut utiliser pour vous via DC Data.",
    loadError: "Impossible de charger les outils.",
    switchesUnknown:
      "Les interrupteurs des outils n'ont pas pu être lus pour le moment. Réessayez dans une minute.",
    access: {
      read: "Lecture",
      write: "Écriture",
    },
    accessLetter: {
      read: "L",
      write: "É",
    },
    comingSoon: "Bientôt disponible",
    paused: "Temporairement désactivé",
    personalSwitchLabel: "Autoriser Claude à utiliser «\u00a0{{title}}\u00a0» pour moi",
    alwaysOn: "Toujours activé",
    unavailable: "Indisponible",
    unavailableReason: {
      connectorOff: "Le connecteur est désactivé",
      needsSignIn: "Uniquement avec la connexion Microsoft",
      writesOff: "Lecture seule dans cet environnement",
      odooNotConnected: "Odoo n'est pas connecté",
      appOff: "L'application {{app}} est désactivée",
    },
    column: {
      tool: "Outil",
      audience: "Pour",
      personal: "Pour moi",
    },
    audience: {
      everyone: "Tout le monde",
      team: "Par équipe",
      admin: "Administrateurs",
      owner: "Propriétaire",
      developer: "Développeurs",
    },
    audienceShort: {
      developer: "Dév.",
    },
    audienceHint: {
      everyone: "Chaque collègue",
      team: "Les membres d'une équipe qui reçoit ce groupe, voir Personnes et équipes",
      admin: "Les administrateurs, le propriétaire et les développeurs",
      owner: "Le propriétaire et les développeurs",
      developer: "Uniquement les développeurs",
    },
    groupCount: {
      tools_one: "{{count, number}} outil",
      tools_many: "{{count, number}} outils",
      tools_other: "{{count, number}} outils",
      read: "{{count, number}} en lecture",
      write: "{{count, number}} en écriture",
      soon: "{{count, number}} à venir",
    },
    kind: {
      odoo: "Odoo",
      account: "Votre compte",
      submission: "Idées et questions",
      role: "Pour votre rôle",
    },
    filter: {
      search: "Rechercher par nom ou description",
      allAccess: "Tous les accès",
      kind: "Type",
      allKinds: "Tous les types",
      group: "Groupe",
      allGroups: "Tous les groupes",
      groupCount_one: "{{count}} groupe",
      groupCount_many: "{{count}} groupes",
      groupCount_other: "{{count}} groupes",
      empty: "Aucun outil trouvé.",
    },
    details: {
      access: "Accès",
      group: "Groupe",
      audience: "Pour",
      size: "Taille",
      sizeValue: "environ {{tokens}} jetons",
      usage: "Utilisation",
      usageLink: "Appels dans Statistiques",
      comingSoon: "Claude ne peut pas encore utiliser cet outil.",
      notServed: "Claude ne reçoit pas cet outil ici.",
      notServedBecause: "Claude ne reçoit pas cet outil ici. {{reason}}.",
      claudeReads: "Ce que Claude lit, en anglais",
      noDescription: "Claude ne reçoit aucune description de cet outil.",
      parameters: "Paramètres",
      noParameters: "Cet outil n'a pas de paramètres.",
      parameter: {
        name: "Paramètre",
        type: "Type",
        required: "Obligatoire",
        default: "Par défaut",
      },
      yes: "Oui",
      no: "Non",
      requiredFact: "obligatoire",
      defaultFact: "par défaut {{value}}",
      loading: "Chargement de la description pour Claude…",
      killSwitch: "Désactivé pour tous",
      killSwitchHint: "Tant que ceci est activé, personne n'obtient cet outil dans Claude.",
    },
  },
  analytics: {
    description:
      "Utilisation du connecteur MCP DC Data dans Claude\u00a0: uniquement des métadonnées, jamais ce qui a été demandé ou répondu.",
    tabs: {
      overview: "Aperçu",
      calls: "Appels",
      failures: "Échecs",
      speed: "Vitesse",
    },
    loadError: "Impossible de charger l'utilisation du connecteur.",
    stale:
      "Impossible d'actualiser les données du connecteur. Vous voyez les dernières données chargées.",
    failedCalls: "Appels en échec",
    shareOfCalls: "{{share}} des appels",
    callsPerDay: "Appels par jour",
    byUser: "Par personne",
    byTool: "Par outil",
    tables: "Tableaux",
    usersEmpty: "Personne n'a utilisé le connecteur sur cette période.",
    usersSearch: "Rechercher par nom ou e-mail",
    usersNoMatch: "Aucune personne trouvée.",
    user: "Utilisateur",
    person: "Personne",
    people: "Personnes",
    everyone: "Tout le monde",
    searchPerson: "Rechercher une personne",
    errors: "Erreurs",
    lastCall: "Dernier appel",
    lastCallFact: "dernier appel {{time}}",
    status: "Statut",
    onlyWithErrors: "Seulement avec erreurs",
    blocked: "Bloqué",
    blockedHint:
      "Velta a retiré à cette personne l'accès à DC Data. Vous bloquez et débloquez les personnes dans Personnes et équipes, sous l'onglet Équipes.",
    withoutSignIn: "Sans connexion",
    allTools: "Tous les outils",
    searchTool: "Rechercher un outil",
    outcome: {
      label: "Résultat",
      all: "Tous les résultats",
      ok: "Réussi",
      failed: "Échoué",
    },
    time: "Heure",
    tool: "Outil",
    duration: "Durée",
    callsLoadError: "Impossible de charger les appels.",
    callsEmpty: "Aucun appel trouvé.",
    ok: "OK",
    error: "Erreur",
    previousPage: "Précédent",
    nextPage: "Suivant",
    toolsEmpty: "Aucun appel d'outil sur cette période.",
    calls: "Appels",
    callsColumn: "Vers les appels",
    callsOf: "Voir les appels de {{name}}",
    meanDuration: "Durée moyenne",
    meanFact: "moyenne {{duration}}",
    failuresFact: "{{failures}} en échec",
    detail: {
      column: "Détails",
      toggle: "Détails de l'appel de {{time}}",
      failure: "Échec\u00a0:",
      toIssue: "Vers le problème",
      noOdoo:
        "Cet appel n'est pas passé par Odoo, ou il date d'avant le 1er novembre 2026, quand DC Data a commencé à conserver les chiffres d'Odoo.",
      figures: {
        odoo_requests: "Requêtes à Odoo",
        odoo_ms: "Temps dans Odoo",
        odoo_max_ms: "Requête la plus lente",
        worker_wait_ms: "Attente d'un processus libre",
        spacing_wait_ms: "Attente de son tour",
        retry_wait_ms: "Attente d'une nouvelle tentative",
        retries: "Nouvelles tentatives",
        rate_limits: "Fois freiné par Odoo",
        odoo_status: "Dernière réponse d'Odoo",
      },
    },
  },
  issues: {
    loadError: "Impossible de charger les échecs du connecteur.",
    perDay: "Appels en échec par jour",
    noFailures: "Aucun appel en échec au cours des {{count}} derniers jours.",
    wholeConnector: "Tout le connecteur",
    roadmapRule: "Non autorisé par les règles de la feuille de route",
    status: {
      new: {
        label: "Nouveau",
        hint: "Velta ne l'a pas encore examiné.",
      },
      regressed: {
        label: "Revenu",
        hint: "Velta l'avait résolu, mais il a de nouveau échoué depuis.",
      },
      open: {
        label: "Ouvert",
        hint: "Rouvert. Velta doit encore l'analyser.",
      },
      acknowledged: {
        label: "En cours",
        hint: "Velta analyse ce problème. Il reste en cours, même s'il échoue à nouveau.",
      },
      resolved: {
        label: "Résolu",
        hint: "Velta l'a résolu. S'il échoue à nouveau, il revient avec le statut Revenu.",
      },
      ignored: {
        label: "Ignoré",
        hint: "Velta le laisse tel quel à dessein, par exemple parce que le connecteur a refusé à raison.",
      },
    },
    category: {
      refused: "Refusé par DC Data",
      request: "Question sans réponse possible",
      odoo: "Erreur d'Odoo",
      timeout: "Délai dépassé",
      cancelled: "Interrompu",
      internal: "Erreur interne",
    },
    kinds: {
      user_blocked: "Personne bloquée",
      user_unidentified: "Personne non reconnue",
      account_not_allowed: "Pas un compte De Ceuster",
      tool_switched_off: "Outil désactivé",
      tool_switched_off_by_user: "Outil désactivé par la personne",
      tool_not_in_team: "Outil hors de l'équipe",
      tool_not_for_role: "Outil non prévu pour ce rôle",
      not_allowed: "Non autorisé",
      roadmap_forbidden: "Non autorisé pour ce rôle",
      tool_not_found: "Outil inconnu",
      unknown_tool: "Outil inconnu",
      invalid_arguments: "Question invalide",
      not_found: "Introuvable",
      refused: "Refusé par l'outil",
      result_too_large: "Réponse trop volumineuse",
      too_many_rows: "Trop de résultats",
      triage_entry_not_found: "Introuvable dans le triage",
      triage_entry_closed: "Déjà fermé dans le triage",
      odoo_unavailable: "Odoo injoignable",
      odoo_error: "Erreur dans Odoo",
      access_denied: "Refusé par Odoo",
      odoo_access_refused: "Odoo refuse l'accès",
      odoo_data_missing: "Manquant dans Odoo",
      odoo_field_missing: "Champ manquant dans Odoo",
      odoo_slow: "Odoo trop lent",
      odoo_busy: "Odoo occupé",
      odoo_time_budget: "Limite de temps atteinte",
      cancelled: "Interrompu par Claude",
      compute_failed: "Calcul échoué",
      unavailable: "Indisponible",
      access_check_unavailable: "Accès impossible à vérifier",
      tool_switches_unavailable: "Interrupteurs illisibles",
      team_access_unavailable: "Équipes illisibles",
      role_unavailable: "Rôle illisible",
      ErrorResult: "Erreur dans la réponse de l'outil",
    },
    list: {
      title: "Problèmes",
      label: "Problèmes",
      search: "Rechercher par problème ou outil",
      category: "Type",
      allStatuses: "Tous les statuts",
      allCategories: "Tous les types",
      noMatch: "Aucun problème trouvé.",
      lastSeenFact: "vu pour la dernière fois {{time}}",
      usersFact_one: "{{count, number}} personne",
      usersFact_many: "{{count, number}} personnes",
      usersFact_other: "{{count, number}} personnes",
    },
    columns: {
      problem: "Problème",
      status: "Statut",
      count: "Fois",
      users: "Personnes",
      lastSeen: "Dernière fois",
      firstSeen: "Première fois",
      perDay: "Par jour",
    },
    page: {
      back: "Tous les échecs",
      loadError: "Impossible de charger ce problème.",
      notFound:
        "Ce problème n'existe pas, ou ses appels en échec ne sont plus conservés. DC Data les conserve 12 mois.",
      statusSince: "statut depuis {{time}}",
      regressedSince: "résolu le {{time}}, puis de nouveau en échec",
      calls: "Appels en échec",
      noFailures: "Aucun appel en échec au cours des {{count}} derniers jours.",
      facts: {
        inPeriod: "Échecs sur cette période",
        shareOfToolCalls: "{{share}} des appels de cet outil",
        total: "Échecs sur 12 mois",
        users: "Personnes sur cette période",
        tools: "Outils sur cette période",
        lastSeen: "Dernière fois",
        firstSeen: "Première fois",
      },
    },
    actions: {
      label: "Actions",
      acknowledge: "Prendre en charge",
      resolve: "Résoudre",
      ignore: "Ignorer",
      reopen: "Rouvrir",
      release: "Remettre en ouvert",
      note: "Ajouter une note",
    },
    dialog: {
      acknowledge:
        "Le problème prend le statut En cours. Les administrateurs voient ainsi que Velta s'en occupe.",
      resolve:
        "Le problème prend le statut Résolu. S'il échoue à nouveau ensuite, il revient avec le statut Revenu.",
      ignore:
        "Le problème prend le statut Ignoré et quitte la page d'accueil, même s'il échoue à nouveau.",
      reopen: "Le problème reprend le statut Ouvert et revient sur la page d'accueil.",
      release:
        "Le problème n'est plus en cours et reprend le statut Ouvert. Il revient ainsi sur la page d'accueil.",
      note: "La note s'ajoute à l'historique du problème. Le statut reste inchangé.",
      noteLabel: "Note",
      noteOptional: "Note (facultative)",
      noteHint:
        "Les administrateurs, propriétaires et développeurs voient la note, avec votre nom.",
      noteRequired: "Saisissez une note.",
      noteTooLong: "Une note compte au plus {{max}} caractères.",
    },
    history: {
      title: "Historique",
      empty: "Personne n'a encore rien fait avec ce problème.",
      byAt: "par {{who}} le {{time}}",
      done: {
        acknowledge: "Pris en charge",
        resolve: "Résolu",
        ignore: "Ignoré",
        reopen: "Rouvert",
        note: "Note",
      },
    },
  },
  speed: {
    loadError: "Impossible de charger la vitesse du connecteur.",
    perDay: "Par jour",
    perTool: "Par outil",
    onlyRateLimited: "Seulement freinés",
    odooCalls: "Appels à Odoo",
    noOdooCalls: "Aucun appel à Odoo au cours des {{count}} derniers jours.",
    total: "Total",
    tooltipRow: "{{label}}\u00a0: {{value}}",
    charts: {
      waitShare: {
        title: "Part du temps d'attente",
        about:
          "La part du temps des appels à Odoo passée chaque jour à attendre un processus libre, son tour ou une nouvelle tentative.",
      },
      waitP95: {
        title: "Attente par appel",
        about:
          "L'attente en deçà de laquelle 95% des appels à Odoo ont eu leur tour chaque jour (le p95).",
      },
      requestP95: {
        title: "Requête la plus lente à Odoo",
        about:
          "Le temps en deçà duquel Odoo a répondu chaque jour même à sa requête la plus lente pour 95% des appels (le p95).",
      },
      rateLimited: {
        title: "Freiné par Odoo",
        about:
          "Combien d'appels Odoo a freinés chaque jour parce qu'il en arrivait trop à la fois.",
      },
    },
    series: {
      worker: "Attente d'un processus libre",
      spacing: "Attente de son tour",
      retry: "Nouvelle tentative",
      wait: "Attente (p95)",
      request: "Requête la plus lente (p95)",
      rateLimited: "Freiné",
    },
    columns: {
      odooCalls: "Appels à Odoo",
      median: "Médiane",
      p95: "p95",
      wait: "Attente (p95)",
      request: "Requête (p95)",
      rateLimited: "Freiné",
    },
    hints: {
      p95: "La durée en deçà de laquelle 95% des appels de l'outil étaient terminés.",
      wait: "L'attente en deçà de laquelle 95% des appels à Odoo de l'outil ont eu leur tour.",
      request:
        "Le temps en deçà duquel Odoo a répondu même à sa requête la plus lente pour 95% des appels de l'outil.",
      rateLimited:
        "Combien d'appels de l'outil Odoo a freinés parce qu'il en arrivait trop à la fois.",
    },
    facts: {
      median: "médiane {{duration}}",
      wait: "attente {{duration}}",
      rateLimited_one: "freiné {{count, number}} fois",
      rateLimited_many: "freiné {{count, number}} fois",
      rateLimited_other: "freiné {{count, number}} fois",
    },
  },
  settings: {
    accountLoadError: "Impossible de charger vos paramètres.",
    roleViewNoAccount:
      "Vous voyez le tableau de bord en tant que rôle. Un rôle n'a pas de compte propre, il n'y a donc ni nom ni activité à afficher.",
    accountInfo: "Informations du compte",
    email: "E-mail",
    role: "Rôle",
    memberSince: "Membre depuis",
    yourActivity: "Votre activité sur le tableau de bord",
    noActivity: "Aucune activité pour le moment.",
    nameTitle: "Modifier le nom",
    firstName: "Prénom",
    lastName: "Nom",
    nameSaved: "Votre nom a été enregistré.",
    aboutDescription:
      "DC Data, le connecteur en lecture seule entre Claude et l'Odoo de De Ceuster, avec ce tableau de bord pour le gérer.",
    version: "Version",
    readMore: "En savoir plus",
    connectClaude: "Connecter Claude",
    privacy: "Vie privée et données",
    roleUser: "Membre",
    roleAdmin: "Administrateur",
    roleOwner: "Propriétaire",
    roleDeveloper: "Développeur",
  },
  viewAs: {
    description:
      "Voyez le tableau de bord tel que quelqu'un d'autre le voit, avec son rôle, ses équipes et ses accès. Tant que vous regardez, vous ne pouvez rien modifier. Chaque début et chaque fin sont inscrits dans le journal d'activité.",
    choice: "Qui vous regardez",
    person: "Une personne",
    role: "Un rôle",
    people: "Personnes",
    search: "Rechercher par nom ou e-mail",
    allRoles: "Tous les rôles",
    roleCount_one: "{{count, number}} rôle",
    roleCount_many: "{{count, number}} rôles",
    roleCount_other: "{{count, number}} rôles",
    searchRole: "Rechercher un rôle",
    column: {
      name: "Nom",
      role: "Rôle",
      teams: "Équipes",
      lastActive: "Dernière activité",
      actions: "Actions",
    },
    noCalls: "Pas encore d'appel",
    lastActiveFact: "Dernière activité le {{time}}",
    action: "Voir en tant que",
    actionFor: "Voir en tant que {{name}}",
    empty: "Personne d'autre n'a encore de compte sur le tableau de bord.",
    noMatch: "Personne ne correspond à ces filtres.",
    loadError: "Impossible de charger les personnes.",
    roles: "Rôles",
    roleAccess: {
      user: "Un nouveau collègue dans aucune équipe\u00a0: seulement ce que tout le monde voit",
      admin: "Chaque page d'administration, et chaque outil et chaque app via Administrateurs",
      owner: "Un administrateur qui voit aussi les heures et les jours sur la feuille de route",
    },
    bar: {
      label: "Voir en tant que",
      person: "Vous voyez le tableau de bord en tant que <strong>{{name}}</strong> ({{role}})",
      personShort: "En tant que <strong>{{name}}</strong> ({{role}})",
      role: {
        user: "Vous voyez le tableau de bord en tant que <strong>{{role}}</strong> sans équipe",
        admin: "Vous voyez le tableau de bord en tant que <strong>{{role}}</strong>",
        owner: "Vous voyez le tableau de bord en tant que <strong>{{role}}</strong>",
      },
      roleShort: {
        user: "En tant que <strong>{{role}}</strong> sans équipe",
        admin: "En tant que <strong>{{role}}</strong>",
        owner: "En tant que <strong>{{role}}</strong>",
      },
      switch: "Changer",
      readOnly: "Lecture seule",
      stop: "Arrêter",
      searchPerson: "Rechercher une personne",
      noPeople: "Aucune personne trouvée.",
    },
    toast: {
      ended: "La vue en tant que quelqu'un d'autre a pris fin.",
      changed: "La vue a été changée dans un autre onglet.",
    },
  },
  people: {
    tabs: {
      users: "Personnes",
      teams: "Équipes",
    },
  },
  users: {
    loadError: "Impossible de charger les utilisateurs.",
    search: "Rechercher par nom ou e-mail",
    allRoles: "Tous les rôles",
    firstSignIn:
      "Les collègues reçoivent un compte lors de leur première connexion avec Microsoft. Un administrateur peut ensuite modifier leur rôle.",
    empty: "Aucun utilisateur trouvé.",
    user: "Utilisateur",
    name: "Nom",
    joined: "Membre depuis",
    actions: "Actions",
    view: "Voir les détails de {{email}}",
    viewShort: "Voir les détails",
    edit: "Modifier {{email}}",
    signOutEverywhereFor: "Déconnecter {{email}} partout",
    deleteFor: "Supprimer {{email}}",
    deleteTitle: "Supprimer l'utilisateur",
    deleteBody:
      "Voulez-vous vraiment supprimer <strong>{{email}}</strong>\u00a0? Cette action est irréversible.",
    detailTitle: "Détails de l'utilisateur",
    detailLoadError: "Impossible de charger les détails de l'utilisateur.",
    updated: "Dernière modification",
    lastSignIn: "Dernière connexion",
    recentActivity: "Activité récente",
    editTitle: "Modifier l'utilisateur",
    signOutEverywhere: "Déconnecter partout",
    revokeSelf:
      "Vous ne pouvez pas vous déconnecter partout à partir d'ici. Utilisez Se déconnecter, ou demandez à un autre administrateur de vous déconnecter partout.",
    roleLockedSelf: "Vous ne pouvez pas modifier votre propre rôle.",
    rolesBecauseOwner: "Parce que vous êtes Propriétaire",
    rolesBecauseDeveloper: "Parce que vous êtes Développeur",
    lockedOwner:
      "Seul un propriétaire ou un développeur peut modifier ou supprimer le compte d'un propriétaire.",
    lockedDeveloper: "Seul un développeur peut modifier ou supprimer le compte d'un développeur.",
    revokeBody:
      "Mettre fin à toutes les sessions actives de <strong>{{email}}</strong>\u00a0? Cette personne devra se reconnecter sur chaque appareil.",
  },
  teams: {
    explainTeams:
      "Les équipes s'additionnent\u00a0: une personne dans plusieurs équipes reçoit l'accès de chacune. Une personne sans équipe reçoit l'accès de Tout le monde sans équipe.",
    explainExceptions:
      "Une exception pour une personne l'emporte toujours sur les équipes. Un outil qu'un développeur désactive temporairement est désactivé pour tout le monde.",
    loadError: "Impossible de charger les équipes.",
    newTeam: "Nouvelle équipe",
    defaultTeam: "Tout le monde sans équipe",
    adminsTeam: "Administrateurs",
    adminsTeamDetail: "Administrateurs, propriétaires et développeurs, avec tous les accès",
    allAccess: "Tous les accès",
    byRole: "par rôle",
    byRoleAndExtra: "par rôle + supplémentaire",
    column: {
      team: "Équipe",
      members: "Membres",
      access: "Accès",
      actions: "Actions",
    },
    filter: {
      search: "Rechercher par nom ou description",
      allAccess: "Tous les groupes et apps",
      noMatch: "Aucune équipe trouvée.",
    },
    peopleWithoutTeam_one: "{{count, number}} personne sans équipe",
    peopleWithoutTeam_many: "{{count, number}} personnes sans équipe",
    peopleWithoutTeam_other: "{{count, number}} personnes sans équipe",
    memberCount_one: "{{count, number}} membre",
    memberCount_many: "{{count, number}} membres",
    memberCount_other: "{{count, number}} membres",
    noMembers: "Pas encore de membres",
    moreMembers: "+{{count, number}}",
    noAccess: "Aucun accès",
    partTitle: "{{group}}\u00a0: {{access}}",
    edit: "Modifier {{team}}",
    deleteFor: "Supprimer {{team}}",
    deleteTitle: "Supprimer l'équipe",
    deleteBody:
      "Voulez-vous vraiment supprimer l'équipe <strong>{{team}}</strong>\u00a0? Ses membres gardent l'accès de leurs autres équipes, ou reçoivent celui de Tout le monde sans équipe.",
    editor: {
      newTitle: "Nouvelle équipe",
      editTitle: "Modifier l'équipe",
      defaultDescription: "Cette équipe n'a pas de membres.",
      name: "Nom de l'équipe",
      description: "Description (facultative)",
      access: "Accès",
      members: "Membres",
      membersHint: "Nous reconnaissons les membres à leur connexion Microsoft (adresse e-mail).",
      comingSoon: "bientôt",
      removeMember: "Retirer {{person}} de l'équipe",
      removeExtra: "Retirer {{person}} des membres supplémentaires",
      noMembers: "Personne dans cette équipe pour l'instant.",
      adminsDescription:
        "Cette équipe a tous les accès, y compris aux outils et aux apps ajoutés plus tard. Une exception pour une personne l'emporte toujours. Cette équipe fixe ne peut être ni renommée ni supprimée.",
      allAccessDetail:
        "Tous les groupes en lecture et en écriture, toutes les apps et les outils réservés aux administrateurs.",
      roleMembers: "Administrateurs, propriétaires et développeurs du tableau de bord",
      roleMembersHint:
        "Ils font partie de l'équipe par leur rôle. Celui-ci se modifie dans l'onglet Personnes.",
      extraMembers: "Membres supplémentaires",
      extraMembersHint:
        "Par exemple des collègues qui utilisent Claude sans compte sur le tableau de bord.",
      addPeople: "Ajouter des personnes",
    },
    people: {
      title: "Personnes et exceptions",
      search: "Rechercher par nom ou e-mail",
      addException: "Ajouter une exception",
      column: {
        person: "Personne",
        teams: "Équipes",
        access: "Accès",
        lastCall: "Dernier appel",
      },
      allTeams: "Toutes les équipes",
      teamCount_one: "{{count, number}} équipe",
      teamCount_many: "{{count, number}} équipes",
      teamCount_other: "{{count, number}} équipes",
      searchTeam: "Rechercher une équipe",
      blocked: "Bloqué",
      withExceptions: "Avec exceptions",
      lastCallFact: "Dernier appel le {{time}}",
      noCalls: "Aucun appel",
      block: "Bloquer",
      unblock: "Débloquer",
      blockFor: "Bloquer {{person}}",
      unblockFor: "Débloquer {{person}}",
      neverSignedIn: "Jamais connecté dans Claude",
      blockedAtBy: "Bloqué le {{time}} par {{email}}.",
      blockedAt: "Bloqué le {{time}}.",
      blockedEffect:
        "DC Data refuse chaque appel de cette personne jusqu'à ce que quelqu'un la débloque.",
      empty:
        "Personne pour l'instant. Les personnes apparaissent ici dès qu'elles sont dans une équipe ou ont utilisé le connecteur.",
      noMatch: "Aucune personne trouvée.",
      exceptions: "Exceptions",
      exceptionsFor: "Exceptions pour {{person}}",
    },
    block: {
      title: "Bloquer l'accès à DC Data",
      unblockTitle: "Débloquer l'accès à DC Data",
      body: "Bloquer l'accès de <strong>{{who}}</strong> au connecteur DC Data\u00a0? Velta coupe son utilisation du connecteur en moins d'une minute. Claude n'obtient alors plus aucun outil, et chaque appel est refusé. Son compte Microsoft lui-même, géré par le prestataire informatique de De Ceuster, reste inchangé.",
      unblockBody:
        "Rendre à <strong>{{who}}</strong> l'accès au connecteur DC Data\u00a0? Cela prend effet en moins d'une minute, tant que son compte Microsoft De Ceuster est encore actif.",
      block: "Bloquer",
      unblock: "Débloquer",
    },
    summary: {
      read: "{{groups}} en lecture",
      write: "{{groups}} en écriture",
      comingSoon: "bientôt",
      partly: "en partie",
      exception: "exception",
      none: "Aucun accès",
    },
    exceptions: {
      description:
        "Autoriser et Refuser l'emportent sur les équipes, et une exception pour un outil l'emporte sur celle de son groupe.",
      teams: "Équipes\u00a0: {{teams}}",
      choice: {
        team: "Selon l'équipe",
        allow: "Autoriser",
        deny: "Refuser",
      },
      allowed: "Accès",
      denied: "Aucun accès",
      partly: "En partie",
      allTools: {
        read: "Tous les outils de lecture",
        write: "Tous les outils d'écriture",
      },
      clearAll: "Tout selon l'équipe",
      choiceLabel: "Exception pour {{unit}}",
    },
    apps: {
      title: "Apps",
      requires: "Requiert {{units}}",
      missing: "Il manque encore {{units}} à cette équipe.",
      units: {
        read: "{{groups}} en lecture",
        write: "{{groups}} en écriture",
      },
      groups: {
        account: "Votre compte",
        clients: "Clients",
        cranes: "Grues",
        orders: "Commandes",
        finance: "Finances",
        system: "Système",
      },
      grant: "App {{app}}",
      summary: "app {{apps}}",
      exceptionMissing: "Il manque {{units}}",
    },
  },
  picker: {
    search: "Rechercher par nom ou e-mail",
    empty: "Aucune personne trouvée.",
    addByEmail: "Ajouter par e-mail",
    invalidEmail: "Tapez une adresse e-mail complète pour ajouter une personne.",
    domainNotAllowed: "Seules les adresses {{domains}} peuvent recevoir un accès.",
  },
  roadmap: {
    newIdea: "Nouvelle idée",
    proposeIdea: "Proposer une idée",
    loadError: "Impossible de charger la feuille de route.",
    empty: "Rien sur la feuille de route pour l'instant. Ajoutez une première idée.",
    emptyPropose: "Rien sur la feuille de route pour l'instant. Proposez une première idée.",
    status: {
      idea: "Idée",
      planned: "Planifié",
      in_progress: "En cours",
      done: "Terminé",
    },
    priority: {
      high: "Haute",
      medium: "Moyenne",
      low: "Basse",
    },
    priorityLong: {
      high: "Priorité haute",
      medium: "Priorité moyenne",
      low: "Priorité basse",
    },
    facts: {
      deadline: "échéance {{date}}",
    },
    groups: {
      done: { title: "Terminé" },
      in_progress: { title: "En cours" },
      planned: { title: "Planifié" },
      idea: { title: "Idées" },
    },
    itemCount_one: "{{count, number}} élément",
    itemCount_many: "{{count, number}} éléments",
    itemCount_other: "{{count, number}} éléments",
    groupTotal: "{{items}}, {{effort}}",
    column: {
      item: "Élément",
      priority: "Priorité",
      effort: "Effort",
      month: "Mois",
      deadline: "Échéance",
      followers: "Abonnés",
    },
    sort: {
      hint: {
        item: "Trier par titre, de A à Z. Un deuxième clic inverse l'ordre, un troisième revient à l'ordre par défaut.",
        priority:
          "Trier par priorité, de basse à haute. Un deuxième clic inverse l'ordre, un troisième revient à l'ordre par défaut.",
        effort:
          "Trier par effort, du plus petit au plus grand. Un deuxième clic inverse l'ordre, un troisième revient à l'ordre par défaut.",
        month:
          "Trier par mois, du plus tôt au plus tard. Un deuxième clic inverse l'ordre, un troisième revient à l'ordre par défaut.",
        deadline:
          "Trier par échéance, de la plus proche à la plus lointaine. Un deuxième clic inverse l'ordre, un troisième revient à l'ordre par défaut.",
        followers:
          "Trier par nombre d'abonnés, du plus petit au plus grand. Un deuxième clic inverse l'ordre, un troisième revient à l'ordre par défaut.",
      },
      by: {
        item: "titre",
        priority: "priorité",
        effort: "effort",
        month: "mois",
        deadline: "échéance",
        followers: "nombre d'abonnés",
      },
      asc: {
        item: "de A à Z",
        priority: "de basse à haute",
        effort: "du plus petit au plus grand",
        month: "du plus tôt au plus tard",
        deadline: "de la plus proche à la plus lointaine",
        followers: "du plus petit au plus grand",
      },
      desc: {
        item: "de Z à A",
        priority: "de haute à basse",
        effort: "du plus grand au plus petit",
        month: "du plus tard au plus tôt",
        deadline: "de la plus lointaine à la plus proche",
        followers: "du plus grand au plus petit",
      },
      active: "Chaque groupe est trié par {{column}}, {{order}}.",
      reset: "Ordre par défaut",
    },
    filter: {
      search: "Rechercher par titre ou description",
      searchDeveloper: "Rechercher par titre, description ou identifiant Linear",
      priority: "Priorité",
      allPriorities: "Toutes les priorités",
      toggles: "Filtres rapides",
      blocked: "Bloqués",
      waitingOnMe: "En attente de moi",
      followedByMe: "Suivis par moi",
      empty: "Aucun élément trouvé.",
    },
    effort: {
      hours: "{{hours}} h",
      day: "{{days}} jour",
      days: "{{days}} jours",
      unknown: "À estimer",
      estimated: "estimé",
      actual: "réel",
      hoursAndDays: "{{hours}} ({{days}})",
      less: "Moins d'effort",
      more: "Plus d'effort",
    },
    blockers: {
      symbol: "Blocage",
      separator: "\u00a0: ",
      itemStatus: "({{status}})",
      more_one: "+{{count, number}} autre blocage",
      more_many: "+{{count, number}} autres blocages",
      more_other: "+{{count, number}} autres blocages",
      nothing: "N'attend rien ni personne",
      since: "depuis le {{date}}",
      resolvedBy: "résolu par {{person}} le {{date}}",
      resolvedOn: "résolu le {{date}}",
      resolve: "Résolu",
      resolveLabel: "Marquer le blocage de {{who}} comme résolu",
      remove: "Supprimer le blocage de {{who}}",
      add: "Blocage",
      addTitle: "Ajouter un blocage",
      who: "Qui",
      kinds: {
        people: "Personnes",
        item: "Élément",
        party: "Autre",
      },
      choosePeople: "Choisir des personnes",
      chooseItem: "Choisir un élément",
      other: "Ce qu'il attend",
      otherPlaceholder: "Par exemple un fournisseur",
      otherTooLong:
        "Le texte sous «\u00a0Autre\u00a0» compte au plus {{max}} caractères. Raccourcissez-le.",
      ask: "Quoi",
      askCount: "{{length}}/{{max}}",
      askCountLabel: "{{length}} sur {{max}} caractères",
      askTooLong: "«\u00a0Quoi\u00a0» compte au plus {{max}} caractères. Raccourcissez-le.",
      askOptional: "Quoi (facultatif)",
      askHint:
        "Formulez-le de sorte qu'il se lise après le nom, par exemple «\u00a0envoyer la liste de prix\u00a0».",
      create: "Ajouter",
      card: "Blocages",
      cardNone: "aucun",
      cardOpen_one: "{{count, number}} ouvert",
      cardOpen_many: "{{count, number}} ouverts",
      cardOpen_other: "{{count, number}} ouverts",
      removeMode: "Supprimer des blocages",
      removeDone: "Fin de la suppression",
      resolvedCount_one: "{{count, number}} résolu",
      resolvedCount_many: "{{count, number}} résolus",
      resolvedCount_other: "{{count, number}} résolus",
    },
    followers: {
      unfollow: "Ne plus suivre",
      followedBy: "Suivi par {{names}}",
      follow: "Suivre",
      followItem: "Suivre {{item}}",
      more: "+{{count, number}}",
      person: "{{name}}, {{role}}",
      none: "Pas encore d'abonnés",
      closed: "Cet élément a été terminé entre-temps, ses abonnés sont donc figés.",
    },
    month: {
      budgetHint: "En heures entières, une journée compte 8 heures.",
      budgetInvalid: "Indiquez un nombre entier d'heures, 0 ou plus.",
      previous: "Mois précédent",
      next: "Mois suivant",
      budget: "Budget",
      done: "Terminé",
      planned: "Encore planifié",
      left: "Restant",
      over: "Au-delà du budget",
      editBudget: "Modifier le budget",
      budgetHours: "Budget de {{month}} en heures",
      legendDone: "Terminé",
      legendPlanned: "Planifié ou en cours",
      legendOver: "Au-delà du budget",
      budgetLine: "Budget {{effort}}",
      chart:
        "En {{month}}, {{done}} de travail terminé et {{planned}} de travail encore planifié, pour un budget de {{budget}}.",
      chartProgress: "En {{month}}, {{done}} et {{planned}}.",
      chartDone_one: "{{count, number}} élément est terminé",
      chartDone_many: "{{count, number}} éléments sont terminés",
      chartDone_other: "{{count, number}} éléments sont terminés",
      chartPlanned_one: "{{count, number}} élément est planifié ou en cours",
      chartPlanned_many: "{{count, number}} éléments sont planifiés ou en cours",
      chartPlanned_other: "{{count, number}} éléments sont planifiés ou en cours",
      segment: "{{title}}, {{hours}}",
      empty: "Rien n'est encore planifié en {{month}}.",
      emptyPast: "Rien n'a été terminé en {{month}}.",
      confirmBody: "Le budget de {{month}} passe de {{from}} à {{to}}.",
      confirmNotify: "L'équipe de développement en sera informée.",
      confirm: "Confirmer",
      budgetForbidden: "Votre rôle ne vous permet pas de modifier le budget de ce mois.",
    },
    details: {
      noSummary: "Pas de description.",
      summary: "Description",
      editContent: "Modifier le titre, la description et la priorité",
      editText: "Modifier le titre et la description",
      editPriority: "Modifier la priorité",
      editingContent: "Modification du titre, de la description et de la priorité",
      editingText: "Modification du titre et de la description",
      editingPriority: "Modification de la priorité",
      stage: "Cycle de vie",
      passed: "(passé)",
      ideaSince: "depuis le {{date}}",
      planIn: "Planifier en",
      planOther: "Autre mois",
      effortByVelta: "Velta choisit d'abord un effort.",
      doneOn: "Terminé le {{date}}",
      waitsStart: "Velta le passe à En cours dès que le travail commence.",
      waitsFinish: "Velta le marque comme terminé dès qu'il est fini.",
      openBlockers_one: "Attend encore {{count, number}} blocage",
      openBlockers_many: "Attend encore {{count, number}} blocages",
      openBlockers_other: "Attend encore {{count, number}} blocages",
      planning: "Planification",
      editPlanning: "Modifier la planification",
      notPlanned: "Pas encore planifié",
      monthAtPlan: "Vous le choisissez à la planification.",
      people: "Personnes",
      effort: "Effort",
      month: "Mois",
      deadline: "Échéance",
      none: "Aucune",
      addedBy: "Ajouté par {{person}} le {{date}}",
      lockedInProgress:
        "Cet élément est en cours de réalisation, son statut, son titre, sa description, son échéance et son mois sont donc figés.",
      lockedDone: "Cet élément est terminé et figé.",
      lockedPast: "Ce mois est passé, cet élément ne peut donc plus être modifié ici.",
      unplan: "Retour aux idées",
      start: "Démarrer",
      unstart: "Retour à planifié",
      finish: "Marquer terminé",
      delete: "Supprimer",
      finishActual: "Heures réelles (facultatif)",
      finishHint: "Laissez vide pour compter l'estimation.",
      deleteBody:
        "Voulez-vous vraiment supprimer <strong>{{item}}</strong>\u00a0? Cette action est irréversible.",
      openItem: "Ouvrir {{item}}",
    },
    statusMenu: {
      plan: "Planifier en {{month}}",
      finish: "Marquer terminé…",
      needsEffort: "Choisissez d'abord un effort",
      trigger: "{{status}}. Changer le statut de {{item}}",
      tooltip: "{{status}}. Cliquez pour changer le statut.",
    },
    form: {
      noDeadline: "Pas d'échéance",
      newTitle: "Nouvelle idée",
      newDescription:
        "Une nouvelle idée arrive directement dans Idées, avec les tickets Linear qui la sous-tendent.",
      proposeDescription:
        "Velta examine chaque proposition et la met sur la feuille de route là où elle a sa place.",
      title: "Titre",
      titleRequired: "Donnez un titre à l'élément.",
      summary: "Description",
      priority: "Priorité",
      priorityOptional: "Priorité (facultatif)",
      noPriority: "Pas de préférence",
      language: "Langue",
      languageHint: "La langue dans laquelle le titre et la description sont rédigés.",
      linear: "Tickets Linear",
      linearPlaceholder: "ABC-12, ABC-13",
      linearHint: "Un ou plusieurs, séparés par des virgules ou des espaces, comme ABC-12, ABC-13.",
      linearRequired: "Indiquez au moins un identifiant Linear, comme ABC-12.",
      linearInvalid_one:
        "{{ids}} n'est pas un identifiant Linear. Un identifiant Linear ressemble à ABC-12.",
      linearInvalid_many:
        "{{ids}} ne sont pas des identifiants Linear. Un identifiant Linear ressemble à ABC-12.",
      linearInvalid_other:
        "{{ids}} ne sont pas des identifiants Linear. Un identifiant Linear ressemble à ABC-12.",
      linearTooMany: "Un élément a au plus {{max}} tickets Linear.",
      effortOptional: "Effort (facultatif)",
      propose: "Proposer",
      proposed:
        "Merci. Velta examine votre idée et la met sur la feuille de route là où elle a sa place.",
      itemSearch: "Rechercher un élément",
      noItems: "Aucun élément trouvé.",
      remove: "Retirer {{name}}",
      create: "Ajouter l'idée",
    },
    developer: {
      noLinear: "Pas encore de tickets.",
      estimateRequired: "Un élément planifié garde une estimation.",
      actualInvalid: "Les heures réelles sont un nombre entier d'heures, 0 ou plus.",
      title: "Développeur",
      edit: "Modifier la partie développeur",
      actual: "Heures réelles",
      actualMissing: "Non renseignées, l'estimation compte.",
      actualAtFinish: "À renseigner avec Marquer terminé.",
      hoursUnit: "h",
      linear: "Tickets Linear",
      linearAdd: "Ajouter l'identifiant Linear",
      linearInput: "Identifiant Linear, comme ABC-123",
      linearInvalid: "Un identifiant Linear ressemble à ABC-123.",
      linearRemove: "Retirer {{id}}",
      linearOpen: "{{id}} dans Linear (s'ouvre dans un nouvel onglet)",
      commandCopy: "Copier la commande",
      commandCopied: "Copiée",
      commandCopiedStatus: "La commande est copiée.",
      commandCopyFailed:
        "La copie n'a pas fonctionné. Sélectionnez la commande et copiez-la vous-même.",
    },
    comments: {
      card: "Commentaires",
      none: "Pas encore de commentaires.",
      loadError: "Impossible de charger les commentaires.",
      input: "Nouveau commentaire",
      placeholder: "Écrivez un commentaire. Tapez @ pour mentionner quelqu'un.",
      post: "Publier",
      mentionList: "Mentionner quelqu'un",
      delete: "Supprimer le commentaire de {{person}}",
      deleteBody:
        "Voulez-vous vraiment supprimer ce commentaire\u00a0? Cette action est irréversible.",
      closed: "Cet élément est terminé, il ne reçoit donc plus de commentaires.",
      count_one: "{{count, number}} commentaire",
      count_many: "{{count, number}} commentaires",
      count_other: "{{count, number}} commentaires",
    },
    triage: {
      title: "Triage",
      kinds: {
        idea: "Idée",
        question: "Question",
      },
      submittedBy: "Soumise par {{person}} le {{date}}",
      reasons: {
        missing_tool: "Outil manquant",
        missing_rights: "Droits manquants",
        odoo_data_missing: "Absent d'Odoo",
        access_withheld: "Accès restreint",
        other: "Autre",
      },
      showDetails: "Détails",
      body: {
        idea: "Description",
        question: "Question",
      },
      toolsTried: "Outils essayés",
      noTools: "Aucun",
      detail: "Note de Claude",
      actions: {
        idea: "Actions pour l'idée de {{person}}",
        question: "Actions pour la question de {{person}}",
      },
      accept: "Vers le backlog",
      acceptLabel: {
        idea: "Mettre l'idée de {{person}} dans le backlog",
        question: "Mettre la question de {{person}} dans le backlog",
      },
      close: "Fermer",
      closeLabel: {
        idea: "Fermer l'idée de {{person}}",
        question: "Fermer la question de {{person}}",
      },
      acceptDescription: {
        idea: "L'élément arrive dans Idées et quitte Triage.",
        question:
          "L'élément arrive dans Idées et la question quitte Triage. Rédigez le titre et la description avec vos propres mots et omettez les données des clients. La question elle-même ne va pas sur la feuille de route.",
      },
      closeTitle: {
        idea: "Fermer l'idée",
        question: "Fermer la question",
      },
      closeDescription: {
        idea: "L'idée quitte Triage sans arriver sur la feuille de route.",
        question: "La question quitte Triage sans arriver sur la feuille de route.",
      },
      closeNote: "Note (facultatif)",
      closeNoteHint:
        "Pourquoi vous la fermez, par exemple «\u00a0déjà répondue\u00a0» ou «\u00a0hors du champ de DC Data\u00a0». La personne qui l’a envoyée lit cette note dans Claude.",
      merge: "Fusionner avec…",
      mergeLabel: {
        idea: "Fusionner l'idée de {{person}} avec un élément",
        question: "Fusionner la question de {{person}} avec un élément",
      },
      mergeTitle: {
        idea: "Fusionner l'idée",
        question: "Fusionner la question",
      },
      mergeDescription: {
        idea: "L'idée quitte Triage et appartient désormais à un élément qui figure déjà sur la feuille de route. La personne qui l'a soumise suit cet élément si elle a accès à la feuille de route.",
        question:
          "La question quitte Triage et appartient désormais à un élément qui figure déjà sur la feuille de route. La question elle-même ne va pas sur la feuille de route.",
      },
      mergeItem: "Élément",
      mergeItemHint: "Tous les éléments qui ne sont pas encore terminés.",
      mergeChoose: "Choisir un élément",
      mergeConfirm: "Fusionner",
      mergeItemDone: "Cet élément est terminé entre-temps. Choisissez-en un autre.",
      mergeItemGone: "Cet élément ne figure plus sur la feuille de route. Choisissez-en un autre.",
      delete: "Supprimer",
      deleteLabel: {
        idea: "Supprimer l'idée de {{person}}",
        question: "Supprimer la question de {{person}}",
      },
      deleteTitle: {
        idea: "Supprimer l'idée",
        question: "Supprimer la question",
      },
      deleteDescription: {
        idea: "L'idée est supprimée définitivement, y compris le texte soumis par {{person}}. Cette action est irréversible. Fermer la retire de Triage sans la supprimer.",
        question:
          "La question est supprimée définitivement, y compris le texte soumis par {{person}}. Cette action est irréversible. Fermer la retire de Triage sans la supprimer.",
      },
    },
  },
  activity: {
    loadError: "Impossible de charger l'activité.",
    events: "Événements",
    logins: "Connexions",
    overTime: "Activité dans le temps",
    allEvents: "Tous les événements",
    byAction: "Par action",
    recent: "Activité récente",
    search: "Rechercher dans l'activité…",
    people: "Personnes",
    allPeople: "Tout le monde",
    searchPerson: "Rechercher une personne",
    actionsFilter: "Actions",
    allActions: "Toutes les actions",
    searchAction: "Rechercher une action",
    previous: "Précédent",
    next: "Suivant",
    page: "Page {{page, number}} sur {{total, number}}",
    action: "Action",
    when: "Date",
    empty: "Aucune activité trouvée.",
    dayTooltip: "{{day}}\u00a0: {{events}}, {{logins}}",
    eventCount_one: "{{count, number}} événement",
    eventCount_many: "{{count, number}} événements",
    eventCount_other: "{{count, number}} événements",
    loginCount_one: "{{count, number}} connexion",
    loginCount_many: "{{count, number}} connexions",
    loginCount_other: "{{count, number}} connexions",
    actions: {
      login: "Connexion",
      signup: "Inscription",
      profile_update: "Nom modifié",
      password_change: "Mot de passe modifié",
      admin_update_user: "Utilisateur modifié",
      admin_delete_user: "Utilisateur supprimé",
      admin_revoke_sessions: "Déconnexion partout",
      admin_invite: "Utilisateur invité",
      admin_first_account: "Premier compte nommé administrateur",
      admin_mcp_enable_tool: "Outil activé",
      admin_mcp_disable_tool: "Outil désactivé",
      admin_mcp_enable_tool_group: "Groupe d'outils activé",
      admin_mcp_disable_tool_group: "Groupe d'outils désactivé",
      admin_mcp_block_user: "Utilisateur du connecteur bloqué",
      admin_mcp_unblock_user: "Utilisateur du connecteur débloqué",
      admin_mcp_team_create: "Équipe créée",
      admin_mcp_team_update: "Équipe modifiée",
      admin_mcp_team_delete: "Équipe supprimée",
      admin_mcp_team_members: "Membres d'équipe modifiés",
      admin_mcp_person_override: "Exception modifiée",
      admin_mcp_issue_acknowledge: "Problème pris en charge",
      admin_mcp_issue_resolve: "Problème résolu",
      admin_mcp_issue_ignore: "Problème ignoré",
      admin_mcp_issue_reopen: "Problème rouvert",
      roadmap_item_create: "Élément de feuille de route ajouté",
      roadmap_item_update: "Élément de feuille de route modifié",
      roadmap_item_status: "Statut d'un élément de feuille de route modifié",
      roadmap_item_delete: "Élément de feuille de route supprimé",
      roadmap_item_effort: "Effort d'un élément de feuille de route modifié",
      roadmap_month_budget: "Budget mensuel modifié",
      roadmap_item_blocker: "Blocage d'un élément de feuille de route modifié",
      roadmap_item_comment: "Commentaire d'un élément de feuille de route ajouté ou supprimé",
      roadmap_gap_promote: "Question transformée en idée de feuille de route",
      roadmap_gap_close: "Question marquée comme répondue",
      roadmap_triage_propose: "Idée de feuille de route proposée",
      roadmap_triage_accept: "Idée ou question du triage mise sur la feuille de route",
      roadmap_triage_close: "Idée ou question du triage fermée ou supprimée",
      view_as_start: "Début de la vue en tant que",
      view_as_switch: "Changement de la vue en tant que",
      view_as_stop: "Fin de la vue en tant que",
    },
  },
  apps: {
    bezetting: {
      title: "Taux d'occupation",
      summary:
        "La part de la flotte louée aujourd'hui, ce qui est réservé et son évolution, par grue, zone et client.",
    },
    maandrapport: {
      title: "Rapport mensuel",
      summary:
        "Le chiffre d'affaires, les clients, les paiements et les prix mois par mois, par rapport à la même période de l'année précédente.",
      emptyBody:
        "Recalculer lit les données dans Odoo et en calcule le rapport mensuel. Cela prend quelques minutes, et vous pouvez continuer à travailler entre-temps.",
      outdatedTitle: "Le rapport mensuel est en cours de mise à jour",
      outdatedBody:
        "Ces données sont recalculées, car le rapport mensuel a une nouvelle version. Cela prend quelques minutes. Si aucun recalcul n'est en cours, choisissez Recalculer.",
      jobErrors: {
        odoo_data_missing:
          "Le recalcul a échoué, car il manquait dans Odoo des factures, des commandes, des paiements ou des prix catalogue.",
      },
    },
    unread_one: "{{count, number}} alerte non lue",
    unread_many: "{{count, number}} alertes non lues",
    unread_other: "{{count, number}} alertes non lues",
    linkWithUnread: "{{app}}, {{unread}}",
    loadError: "Impossible de charger {{app}}.",
    meldingenError: "Impossible de charger les alertes. Cette page ne les affiche donc pas.",
    pageError: "Une partie de cette page n'a pas pu s'afficher. Rechargez la page pour réessayer.",
    previousKept: "Vous voyez encore les données précédentes.",
    denied: {
      title: "Pas d'accès",
      contactsTitle: "Qui donne l'accès",
      noContacts: "Demandez-le à un administrateur du tableau de bord.",
    },
    empty: {
      title: "Pas encore de données",
      body: "Recalculer lit les données dans Odoo et en calcule le taux d'occupation. Cela prend quelques minutes, et vous pouvez continuer à travailler entre-temps.",
    },
    recompute: {
      start: "Recalculer",
      running: "Recalcul en cours…",
      waiting: "Nouveau recalcul possible à {{time}}",
      unavailable: "Le recalcul est impossible tant qu'Odoo n'est pas relié",
      runningPercent: "Recalcul en cours, {{percent}}",
    },
    progress: {
      title: "Recalculer",
      lead: "Les chiffres actuels restent visibles jusqu'à ce que les nouveaux soient prêts.",
      leadFirst: "Les chiffres apparaissent sur cette page dès qu'ils sont prêts.",
      label: "Progression du recalcul",
      running: "En cours depuis {{time}}",
      finished: "Terminé en {{time}}",
      stopped: "Arrêté après {{time}}",
      done: "Terminé. La page affiche maintenant l'état d'Odoo de {{time}}.",
      notLoaded: "Les nouveaux chiffres sont calculés, mais ils n'ont pas encore pu être chargés.",
      hide: "Masquer",
      steps: {
        odoo_read: "Lecture d'Odoo",
        check: "Contrôle des données",
        build: "Calcul des pages",
        store: "Enregistrement",
      },
      status: {
        pending: "pas encore commencé",
        active: "en cours",
        done: "terminé",
        failed: "échoué",
      },
      read_one: "{{table}}, {{done, number}} sur {{count, number}} enregistrement",
      read_many: "{{table}}, {{done, number}} sur {{count, number}} enregistrements",
      read_other: "{{table}}, {{done, number}} sur {{count, number}} enregistrements",
      readCount_one: "{{table}}, {{count, number}} enregistrement",
      readCount_many: "{{table}}, {{count, number}} enregistrements",
      readCount_other: "{{table}}, {{count, number}} enregistrements",
      tables: {
        sale_order: "Commandes",
        sale_order_line: "Lignes de commande",
        account_move: "Factures",
        account_move_line: "Lignes de facture",
        res_partner: "Clients et contacts",
        fleet_vehicle: "Grues",
        product_product: "Produits",
        res_company: "Sociétés",
        sale_order_log: "Historique des commandes",
        account_partial_reconcile: "Lettrage des paiements",
        product_pricing: "Prix de location",
        receivable_line: "Postes ouverts",
        stock_picking: "Livraisons",
        sale_pricing_crane_transport: "Tarifs de transport",
        fleet_vehicle_model: "Types de grue",
        account_analytic_account: "Comptes analytiques",
      },
      parts: {
        fleet: "Grues et locations",
        geo: "Zone d'activité",
        places: "Chantiers et lieux",
        pages: "Assemblage des pages",
        crane_tables: "Tableaux des grues",
        finish: "Finalisation et contrôle",
        omzet: "Chiffre d'affaires",
        klanten: "Clients",
        betalingen: "Factures et paiements",
        prijzen: "Prix",
        ongefactureerd: "Travail non facturé",
      },
    },
    jobErrors: {
      odoo_not_configured: "Le recalcul a échoué, car Odoo n'est pas relié.",
      odoo_unavailable:
        "Le recalcul a échoué, car Odoo n'a pas répondu. Réessayez dans quelques minutes.",
      odoo_access_refused:
        "Le recalcul a échoué, car le compte Odoo ne peut pas lire toutes les données nécessaires.",
      odoo_field_missing: "Le recalcul a échoué, car un champ manque ou a changé dans Odoo.",
      odoo_time_budget:
        "Le recalcul a échoué, car la lecture d'Odoo a pris trop de temps. Réessayez plus tard.",
      odoo_data_missing:
        "Le recalcul a échoué, car Odoo a renvoyé beaucoup moins de grues que d'habitude, ou aucune location.",
      compute_failed: "Le recalcul a échoué, car le calcul s'est interrompu.",
      snapshot_invalid:
        "Le recalcul a échoué, car le résultat était incohérent. Il n'a pas été enregistré.",
      store_failed: "Le recalcul a échoué, car le résultat n'a pas pu être enregistré.",
      abandoned: "Le recalcul a été arrêté, car il prenait trop de temps.",
      interrupted: "Le recalcul a été interrompu, car le serveur a redémarré.",
      unknown: "Le recalcul a échoué.",
    },
  },
  login: {
    title: "Se connecter à {{app}}",
    microsoft: "Se connecter avec Microsoft",
    hint: "Connectez-vous avec votre compte Microsoft De Ceuster.",
    microsoftErrors: {
      microsoft_cancelled:
        "La connexion avec Microsoft a été annulée, ou votre compte n'a pas accès à cette application.",
      microsoft_failed: "La connexion avec Microsoft a échoué. Veuillez réessayer.",
      email_not_verified:
        "Votre compte Microsoft n'a pas d'adresse e-mail vérifiée. Adressez-vous à votre administrateur informatique.",
      domain_not_allowed: "Ce domaine e-mail n'est pas autorisé à se connecter.",
      no_account: "Aucun compte n'a été trouvé pour cet utilisateur Microsoft.",
    },
  },
  errors: {
    codes: {
      not_authenticated: "Vous n'êtes pas connecté. Veuillez vous reconnecter.",
      invalid_session: "Votre session a expiré. Veuillez vous reconnecter.",
      session_revoked: "Votre session a pris fin. Veuillez vous reconnecter.",
      account_not_found: "Aucun compte n'a été trouvé. Demandez l'accès à un administrateur.",
      admin_required: "Seul un administrateur peut effectuer cette action.",
      owner_required: "Seul un propriétaire peut effectuer cette action.",
      developer_required: "Seul un développeur peut effectuer cette action.",
      view_as_read_only:
        "Vous voyez le tableau de bord en tant que quelqu'un d'autre. Tant que vous regardez, vous ne pouvez rien modifier.",
      view_as_self: "Vous ne pouvez pas voir le tableau de bord en tant que vous-même.",
      view_as_ended:
        "Vous ne voyez plus le tableau de bord en tant que quelqu'un d'autre. La page se recharge.",
      auth_unavailable:
        "Votre compte n'a pas pu être vérifié pour le moment. Réessayez dans une minute.",
      revoke_failed: "Les sessions n'ont pas pu être fermées. Rien n'a été modifié.",
      user_not_found: "Cet utilisateur n'existe plus.",
      invalid_role: "Ce rôle n'existe pas.",
      cannot_demote_self: "Vous ne pouvez pas modifier votre propre rôle.",
      cannot_delete_self: "Vous ne pouvez pas supprimer votre propre compte.",
      cannot_revoke_self: "Vous ne pouvez pas vous déconnecter partout à partir d'ici.",
      role_assignment_forbidden: "Votre rôle ne vous permet pas d'attribuer ce rôle.",
      user_change_forbidden: "Votre rôle ne vous permet pas de modifier ou de supprimer ce compte.",
      tool_not_found: "Cet outil n'existe plus.",
      tool_not_switchable: "Cet outil ne peut pas être désactivé.",
      team_not_found: "Cette équipe n'existe plus.",
      team_name_taken: "Une équipe porte déjà ce nom.",
      team_not_deletable: "Tout le monde sans équipe ne peut pas être supprimé.",
      team_not_editable: "Pour Tout le monde sans équipe, seul l'accès peut être modifié.",
      team_member_not_allowed:
        "Seules les adresses e-mail de De Ceuster peuvent rejoindre une équipe.",
      person_not_allowed:
        "Les exceptions ne sont possibles que pour les adresses e-mail de De Ceuster.",
      unit_unknown: "Un de ces outils ou groupes n'existe plus. Rechargez la page.",
      service_unavailable: "Le service est momentanément indisponible. Réessayez dans une minute.",
      app_access_required: "Vous n'avez pas accès à cette app. Demandez-le à un administrateur.",
      app_not_for_default_team:
        "Tout le monde sans équipe ne peut pas recevoir d'app. Donnez l'app à une équipe.",
      admins_team_not_deletable:
        "Administrateurs est une équipe fixe et ne peut pas être supprimée.",
      admins_team_not_editable:
        "Pour Administrateurs, seuls les membres supplémentaires peuvent être modifiés.",
      snapshot_not_found: "Ces données ont été remplacées entre-temps. Rechargez la page.",
      section_not_found: "Cette partie des données n'existe pas.",
      job_not_found: "Ce recalcul n'existe plus.",
      recompute_running: "Un recalcul est déjà en cours.",
      recompute_too_soon:
        "Les données viennent d'être recalculées. Réessayez dans quelques minutes.",
      odoo_not_configured: "Odoo n'est pas relié, le recalcul est donc impossible.",
      snapshot_outdated:
        "Le rapport mensuel est recalculé pour la nouvelle version. Cela prend quelques minutes.",
      invalid_range: "Cette période ne peut pas être affichée. Choisissez une autre période.",
      detail_not_found: "Il n'y a pas de données pour ce choix.",
      client_not_found: "Il n'y a pas de données sur ce client.",
      roadmap_item_not_found: "Cet élément ne figure plus sur la feuille de route.",
      roadmap_forbidden: "Votre rôle ne vous permet pas de modifier cet élément de cette façon.",
      roadmap_transition_not_allowed:
        "Cet élément ne peut pas passer à ce statut. Rechargez la page pour voir où il en est.",
      roadmap_estimate_required: "Cet élément doit d'abord être estimé avant d'être planifié.",
      roadmap_waits_loop:
        "Cet élément ne peut pas attendre un élément qui attend lui-même celui-ci.",
      roadmap_unknown_person: "Une de ces personnes n'a pas de compte sur le tableau de bord.",
      roadmap_linear_id_taken: "Ce ticket Linear appartient déjà à un autre élément.",
      roadmap_effort_not_a_step: "Choisissez un effort dans la liste.",
      roadmap_blocker_not_found: "Ce blocage ne figure plus sur cet élément.",
      roadmap_blocker_invalid:
        "Choisissez qui ou quoi bloque l'élément, et indiquez ce qu'il faut faire.",
      roadmap_item_done: "Cet élément est terminé, il ne reçoit donc plus de commentaires.",
      roadmap_mention_invalid:
        "Vous ne pouvez mentionner que les administrateurs, propriétaires et développeurs du tableau de bord.",
      roadmap_comment_not_found: "Ce commentaire n'existe plus.",
      roadmap_triage_not_found: "Cette idée ou question n'est plus dans Triage.",
      roadmap_triage_closed:
        "Cette idée ou question a déjà été mise sur la feuille de route ou fermée.",
      tool_issue_not_found:
        "Ce problème n'existe pas, ou ses appels échoués ne sont plus conservés.",
    },
    fallback: {
      loadToolDefinition: "Impossible de charger la description pour Claude.",
      updateProfile: "Impossible d'enregistrer votre nom.",
      updateUser: "Impossible de mettre à jour l'utilisateur.",
      deleteUser: "Impossible de supprimer l'utilisateur.",
      revokeSessions: "Impossible de déconnecter l'utilisateur.",
      blockUser: "Impossible de bloquer cette personne.",
      unblockUser: "Impossible de débloquer cette personne.",
      issueAction: "Impossible de mettre à jour ce problème.",
      switchToolOn: "Impossible d'activer cet outil. Réessayez dans une minute.",
      switchToolOff: "Impossible de désactiver cet outil. Réessayez dans une minute.",
      saveTeam: "Impossible d'enregistrer l'équipe.",
      deleteTeam: "Impossible de supprimer l'équipe.",
      saveExceptions: "Impossible d'enregistrer les exceptions.",
      recompute: "Impossible de lancer le recalcul. Réessayez dans une minute.",
      saveMeldingen: "Impossible d'enregistrer l'alerte. Réessayez.",
      appData: "Impossible de charger les données. Rechargez la page.",
      createRoadmapItem: "Impossible d'ajouter l'idée.",
      updateRoadmapItem: "Impossible d'enregistrer l'élément.",
      setRoadmapStatus: "Impossible de modifier le statut.",
      setRoadmapEffort: "Impossible d'enregistrer l'effort.",
      followRoadmapItem: "Impossible de modifier le suivi de cet élément. Réessayez.",
      deleteRoadmapItem: "Impossible de supprimer l'élément.",
      setRoadmapBudget: "Impossible d'enregistrer le budget.",
      addRoadmapBlocker: "Impossible d'ajouter le blocage.",
      resolveRoadmapBlocker: "Impossible de marquer le blocage comme résolu.",
      deleteRoadmapBlocker: "Impossible de supprimer le blocage.",
      addRoadmapComment: "Impossible de publier le commentaire.",
      deleteRoadmapComment: "Impossible de supprimer le commentaire.",
      proposeRoadmapIdea: "Impossible de proposer l'idée.",
      acceptRoadmapTriage: "Impossible de mettre l'élément dans le backlog.",
      closeRoadmapTriage: "Impossible de fermer l'élément.",
      mergeRoadmapTriage: "Impossible de fusionner l'élément.",
      deleteRoadmapTriage: "Impossible de supprimer l'élément.",
      startViewAs: "Impossible de commencer à voir en tant que. Réessayez.",
      stopViewAs: "Impossible d'arrêter de voir en tant que. Réessayez.",
    },
  },
};
